import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";
import {
  isThreadConstraintsActionable,
  useThreadConstraintsState,
} from "../lifecycle/useThreadConstraintsState";
import styles from "./constraintsActionButtons.module.scss";

type ConstraintsActionButtonsProps = {
  enableSubmitButton: boolean;
  runInProgress: boolean;
  onSubmit: () => void;
  onReset: () => void;
  onSave: () => void;
  saveDisabled: boolean;
  hasResetOccurred?: boolean;
};

export const ConstraintsActionButtons: React.FC<
  ConstraintsActionButtonsProps
> = ({ onSubmit, onReset, onSave, saveDisabled, enableSubmitButton, runInProgress, hasResetOccurred }) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  // The thread is "actionable" only when its constraints state is settled.
  // While the new SDK provider is still delivering the navigated-to thread's
  // metadata (`connecting`) or a silent metadata write is in flight
  // (`syncing`), every action button blocks. This replaces a class of races
  // where the user could click Invia / Salva / Reset before the panel
  // reflected the real thread state.
  const threadConstraintsState = useThreadConstraintsState();
  const lifecycleBlocked = !isThreadConstraintsActionable(threadConstraintsState);

  const resetDisabled = runInProgress || lifecycleBlocked;
  const submitDisabled = !(enableSubmitButton || hasResetOccurred) || runInProgress || lifecycleBlocked;
  const saveButtonDisabled = saveDisabled || runInProgress || lifecycleBlocked;

  return (
    <div className={styles.container}>
      <div className={styles.primaryRow}>
        <button
          type="button"
          className={styles.constraintSubmitButton}
          onClick={onSave}
          disabled={saveButtonDisabled}
          style={{
            backgroundColor: colorsConfig.white,
            color: colorsConfig.FinecoBluePrimary,
            opacity: saveButtonDisabled ? 0.4 : 1,
          }}
        >
          {t("savedProfiles.save")}
        </button>
        <button
          type="button"
          className={styles.constraintSubmitButton}
          onClick={onSubmit}
          disabled={submitDisabled}
          style={{
            backgroundColor: colorsConfig.finecoBluePrimary,
            color: colorsConfig.white,
            opacity: submitDisabled ? 0.4 : 1,
          }}
        >
          {t("actions.submit")}
        </button>
      </div>
      <button
        type="button"
        className={styles.resetLink}
        onClick={onReset}
        disabled={resetDisabled}
        style={{
          color: colorsConfig.FinecoBluePrimary,
          opacity: resetDisabled ? 0.4 : 1,
          cursor: resetDisabled ? 'default' : 'pointer',
        }}
      >
        {t("actions.reset")}
      </button>
    </div>
  );
};
